import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { CampaignLimits } from '../core/data/campaign-limits';
import { Payment } from '../core/data/payment/payment';
import { PaymentService } from '../core/data/payment/payment.service';
import { MakePaymentModalResultEnum } from './make-payment-modal-result-type';
import { MakePaymentModalResult } from './make-payment-modal.result';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ss-make-payment-modal-component',
    templateUrl: './make-payment-modal.component.html',
    styleUrls: ['./make-payment-modal.component.css']
})
export class MakePaymentModalComponent implements OnInit {

    @Input() payment: Payment;

    @Output() closed = new EventEmitter<MakePaymentModalResult>();

    errorTitle = new BehaviorSubject<string | null>(null);
    errorMsg1 = new BehaviorSubject<string | null>(null);
    errorMsg2 = new BehaviorSubject<string | null>(null);
    errorDetails = new BehaviorSubject<string | null>(null);
    newIdempotentKey = '';

    // We prepare the modal return value when the operation completes and cache it here.
    // When the user clicks X, then we finally emit the value.
    private retval = new MakePaymentModalResult(null, MakePaymentModalResultEnum.FailureAllowRetry);

    vm$ = combineLatest([this.errorTitle, this.errorMsg1, this.errorMsg2, this.errorDetails])
        .pipe(map(([errorTitle, errorMsg1, errorMsg2, errorDetails]) => ({
            errorTitle,
            errorMsg1,
            errorMsg2,
            errorDetails,
            hasError: !!errorMsg1
        })));

    constructor(
        private paymentService: PaymentService
    ) { }

    ngOnInit() {
        const s = this.paymentService.getNewId(true).subscribe(d => { this.newIdempotentKey = d; }, undefined, () => { s.unsubscribe(); });

        this.paymentService.makePayment(this.payment, true, '*')
            .then(d => {
                this.closed.emit(new MakePaymentModalResult(d, MakePaymentModalResultEnum.Success));
            })
            .catch(e => {
                let resultType = MakePaymentModalResultEnum.FailureAllowRetry;
                let campaignLimits: CampaignLimits | null = null;

                this.errorTitle.next('The payment was not processed');
                this.errorMsg1.next(null);
                this.errorMsg2.next(null);
                this.errorDetails.next(null);

                if (e.ccTokenizeFailure) {
                    // We failed to tokenize the credit-card information.
                    this.errorMsg1.next('Unfortunately, we were unable to verify the credit-card.  Feel free to update your payment information and try again.');

                } else {
                    switch (e.status) {
                        case 400:
                            switch (e.error?.errorType) {
                                case 'campaignCap':
                                    campaignLimits = e.error?.campaignLimits;
                                    this.errorMsg1.next('Thank you for your generosity.  Unfortunately, the donation limit has been reached for this campaign.');
                                    this.errorMsg2.next('Feel free to lower the donation amount and try again.');
                                    break;
                                default:
                                    this.errorMsg1.next('An error prevented the payment from being processed.  Feel free to update your payment information and try again.');
                                    break;
                            }

                            break;
                        case 422:
                            this.errorMsg1.next('Unfortunately, the payment was declined.  Feel free to update your payment information and try again.');
                            break;
                        case 504:
                            {
                                this.errorTitle.next('The payment timed out');

                                this.errorMsg1.next('Unfortunately, a timeout occurred and we are unable to confirm if the payment went through.  Please do NOT retry.');
                                this.errorMsg2.next('If you would like confirmation of the donation, please contact the school.');

                                resultType = MakePaymentModalResultEnum.FailureNoRetry;
                                break;
                            }
                        default:
                            {
                                this.errorTitle.next('Unexpected error');

                                this.errorMsg1.next(
                                    'Unfortunately, an unexpected error occurred and we are unable to confirm if the payment went through.  Please do NOT retry.'
                                );

                                let msg2 = 'If you would like further assistance, please reach out the school';

                                const error = e.error;

                                const messages = [];
                                if (error.origin) messages.push('Origin: ' + error.origin);
                                if (error.timeStamp) messages.push('Time Stamp: ' + error.timeStamp);
                                if (error.errorId) messages.push('Error ID: ' + error.errorId);
                                if (error.code) messages.push('Code: ' + error.code);
                                if (error.message) messages.push('Message: ' + error.message);
                                if (error.exceptionMessage) messages.push('Exception Message: ' + error.exceptionMessage);
                                if (error.exceptionType) messages.push('Exception Type: ' + error.exceptionType);
                                if (error.stackTrace) messages.push('Stack Trace: ' + error.stackTrace);

                                if (messages.length > 0) {
                                    msg2 += ' with the following:';

                                    this.errorDetails.next(messages.join('<br/>'));
                                } else {
                                    msg2 += '.';
                                }

                                this.errorMsg2.next(
                                    msg2
                                );

                                resultType = MakePaymentModalResultEnum.FailureNoRetry;
                                break;
                            }
                    }
                }

                // Prepare the return value.  Once the user clicks X, we will then emit this value.
                // See closeModal().

                this.retval = new MakePaymentModalResult(campaignLimits, resultType);
            });
    }

    async closeModal() {
        if (!this.newIdempotentKey || !this.newIdempotentKey.length) {
            // This should rarely happen.  This means we attempted to process the payment, showed an error, and
            // waited for the user all BEFORE the newid() request we sent at form load had a chance to complete.
            // Remarkable.
            this.newIdempotentKey = await this.paymentService.getNewId(false).pipe(first()).toPromise() ?? '';
        }

        this.retval.newIdempotentKey = this.newIdempotentKey;

        this.closed.emit(this.retval);
    }
}
